import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { SprayCard } from '../../components/spray';
import { Droplets, Plus, FlaskConical } from 'lucide-react';
import SprayNav from '../../components/spray/SprayNav';
import sprayService from '../../services/spray';
import type { SprayApplication, SprayStatus, MyAdvisoryOrg } from '../../types/spray';

const STATUS_TABS: { label: string; value: SprayStatus | 'all' }[] = [
  { label: 'All',         value: 'all' },
  { label: 'Created',     value: 'created' },
  { label: 'Assigned',    value: 'assigned' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Completed',   value: 'completed' },
  { label: 'Cancelled',   value: 'cancelled' },
];

function SprayDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applications, setApplications] = useState<SprayApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState<SprayStatus | 'all'>('all');
  const [advisoryOrgs, setAdvisoryOrgs] = useState<MyAdvisoryOrg[]>([]);
  const [selectedAdvisoryOrg, setSelectedAdvisoryOrg] = useState<number | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const LIMIT = 20;

  // Load advisor orgs; reload when org changes
  useEffect(() => {
    setAdvisoryOrgs([]);
    setSelectedAdvisoryOrg(null);
    sprayService.getMyAdvisoryOrgs().then(res => {
      if (res.success && res.data) setAdvisoryOrgs(res.data as MyAdvisoryOrg[]);
    });
  }, [user?.organisation_id]);

  // Set target org when advisor selection changes
  useEffect(() => {
    sprayService.setTargetOrg(selectedAdvisoryOrg);
  }, [selectedAdvisoryOrg]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const filters = {
        status: activeStatus === 'all' ? undefined : activeStatus,
        page,
        limit: LIMIT,
      };
      const res = await sprayService.getApplications(filters);
      if (res.success) {
        setApplications((res as any).applications || res.data || []);
        setTotal((res as any).total || 0);
      }
    } catch (e) {
      console.error('Load sprays error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [activeStatus, selectedAdvisoryOrg]);

  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatus, selectedAdvisoryOrg, page]);

  const handleStart = async (id: number) => {
    const res = await sprayService.startApplication(id);
    if (res.success) {
      navigate(`/spray/${id}`);
    }
  };

  const handleResume = async (id: number) => {
    const res = await sprayService.resumeApplication(id);
    if (res.success) {
      navigate(`/spray/${id}`);
    }
  };

  const handleComplete = (id: number) => {
    navigate(`/spray/${id}#complete`);
  };

  const orgName = selectedAdvisoryOrg
    ? advisoryOrgs.find(o => o.target_org_id === selectedAdvisoryOrg)?.target_org_name
    : user?.organisation_name;

  return (
    <>
      <PageHeader
        title="Spray Management"
        subtitle={orgName ? `${orgName} · Spray records` : 'Spray records'}
        icon={<FlaskConical className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex items-center gap-2">
            {advisoryOrgs.length > 0 && (
              <select
                value={selectedAdvisoryOrg ?? ''}
                onChange={e => setSelectedAdvisoryOrg(e.target.value ? parseInt(e.target.value) : null)}
                className="border border-neutral-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">My organisation</option>
                {advisoryOrgs.map(org => (
                  <option key={org.target_org_id} value={org.target_org_id}>{org.target_org_name}</option>
                ))}
              </select>
            )}
            <Button variant="primary" size="sm" onClick={() => navigate('/spray/new')}>
              <Plus className="h-4 w-4" />
              New Spray
            </Button>
          </div>
        }
      />
      <SprayNav />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Status filter tabs */}
        <div className="flex gap-1 flex-wrap mb-6">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveStatus(tab.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeStatus === tab.value
                ? 'bg-primary-600 text-white font-medium'
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Applications grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-52 bg-neutral-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Droplets className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No spray applications found</p>
            <p className="text-sm mt-1">
              {activeStatus !== 'all' ? 'Try changing the filter above.' : 'Create your first spray to get started.'}
            </p>
            {activeStatus === 'all' && (
              <div className="mt-4 flex flex-col items-center gap-3">
                <Button variant="primary" onClick={() => navigate('/spray/new')}>
                  <Plus className="h-4 w-4" /> Create Spray
                </Button>
                <p className="text-xs text-neutral-400">
                  Need to add chemicals first?{' '}
                  <button
                    onClick={() => navigate('/spray/chemicals')}
                    className="text-primary-600 hover:underline"
                  >
                    Go to Chemical Registry →
                  </button>
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {applications.map(spray => (
                <SprayCard
                  key={spray.id}
                  spray={spray}
                  currentUserId={user?.id}
                  onStart={handleStart}
                  onResume={handleResume}
                  onComplete={handleComplete}
                />
              ))}
            </div>

            {/* Pagination */}
            {total > LIMIT && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-neutral-500">
                  Page {page} of {Math.ceil(total / LIMIT)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * LIMIT >= total}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default SprayDashboard;
